import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { UserAccount } from '../../store/models';
import { AccountStore } from '../../store/stores';
import {
  filter,
  flatMap,
  map,
  shareReplay,
  take,
  takeUntil,
} from 'rxjs/operators';
import { LocalizationStore } from '../../../localization/store/stores';
import { AccountDispatcher } from '../../store/dispatchers';
import { IAccountResponseError } from '../../account.data';
import { TrackingScreenNamesEnum } from '../../../tracking/tracking.data';
import { I2gNgTrackingService, ITrackingObject } from 'i2g-ng-tracking';

@Component({
  selector: 'app-select-account',
  templateUrl: './select-account.component.html',
  styleUrls: ['./select-account.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SelectAccountComponent
  implements OnInit, OnDestroy, AfterViewInit {
  private _destroyed: Subject<boolean> = new Subject<boolean>();

  userAccount$: Observable<UserAccount>;
  userAccount: UserAccount;
  processing$: Observable<boolean>;
  error$: Observable<string | Error | IAccountResponseError>;
  trackingScreenName: TrackingScreenNamesEnum;
  trackingObject$: Observable<ITrackingObject>;

  constructor(
    private _accountStore: AccountStore,
    private _accountDispatchers: AccountDispatcher,
    private _localizationStore: LocalizationStore,
    private _trackingService: I2gNgTrackingService
  ) {}

  ngOnInit() {
    this.trackingScreenName = TrackingScreenNamesEnum.choose_existing_account;
    this.trackingObject$ = this._accountStore
      .getTrackingObject()
      .pipe(shareReplay(1));

    this.userAccount$ = this._accountStore.getUserAccount().pipe(
      takeUntil(this._destroyed),
      filter((x) => !!x),
      shareReplay(1)
    );

    this.userAccount$.subscribe(
      (userAccount) => (this.userAccount = userAccount)
    );

    this.processing$ = this._accountStore
      .isAccountLoggingIn()
      .pipe(takeUntil(this._destroyed), shareReplay(1));

    this.error$ = this._accountStore.getAccountError().pipe(
      takeUntil(this._destroyed),
      map((error: Error | IAccountResponseError) => {
        if (error) {
          return error instanceof Error ? error.message : error.description;
        }
        return error;
      }),
      shareReplay(1)
    );
  }

  ngAfterViewInit() {
    this._trackingService.trackPage(this.trackingScreenName);
  }

  loginAccountByCompany(accountId: string) {
    this.processing$
      .pipe(
        filter((processing) => !processing),
        flatMap(() => this._localizationStore.getCurrentLocale()),
        take(1)
      )
      .subscribe((locale: string) =>
        this._accountDispatchers.loginAccountByCompany(
          accountId,
          this.userAccount.refreshToken,
          locale,
          this.trackingScreenName
        )
      );
  }

  ngOnDestroy() {
    this._destroyed.next(true);
    this._destroyed.unsubscribe();
  }
}
